import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Offcanvas } from "react-bootstrap";
import Select from "react-select";
import { IconCalendarEvent, IconInfoCircle, IconUsers } from "@tabler/icons-react";
import { useUser } from "contexts/v1/masterData/UserContext";
import { getRequest, postRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const getData = (response) => response?.data?.data || response?.data || [];

const RecapStat = ({ label, value, icon, tone }) => (
  <div className="col-sm-6 col-lg-3">
    <div className="card card-sm border shadow-none h-100">
      <div className="card-body py-2">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <div className="text-muted small">{label}</div>
            <div className="h3 m-0">{value}</div>
          </div>
          <div className={`avatar bg-${tone}-lt text-${tone}`}>{icon}</div>
        </div>
      </div>
    </div>
  </div>
);

const EmptyRow = ({ colSpan, message }) => (
  <tr>
    <td colSpan={colSpan} className="text-center py-5">
      <div className="empty">
        <div className="empty-icon">
          <IconInfoCircle size={36} className="text-muted opacity-25" />
        </div>
        <p className="empty-title mb-1">Belum ada data</p>
        <p className="empty-subtitle text-muted mb-0">{message}</p>
      </div>
    </td>
  </tr>
);

const BatchUseHolidayModal = ({ show, onClose, holidayPackage, users, onSuccess }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [dates, setDates] = useState([""]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (show) {
      setSelectedIds([]);
      setDates([""]);
      setError("");
    }
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedIds.length || !dates.length) return;
    const validDates = dates.map((d) => (d ? String(d).slice(0, 10) : "")).filter(Boolean);
    if (!validDates.length) return;

    setSaving(true);
    setError("");
    try {
      await postRequest(`/ahr/holiday-packages/${holidayPackage.id}/schedule-batch`, {
        status: "draft",
        user_ids: selectedIds,
        dates: validDates,
      });
      onSuccess();
    } catch (err) {
      setError(err.message || "Gagal menerapkan batch schedule PH.");
    } finally {
      setSaving(false);
    }
  };

  const toggleUser = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Offcanvas show={show} onHide={onClose} placement="end" style={{ width: "450px" }}>
      <form onSubmit={handleSubmit} className="d-flex flex-column h-100">
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title>Batch Gunakan PH</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="d-flex flex-column p-0 bg-light">
          <div className="p-3 bg-white border-bottom">
            <div className="fw-bold mb-1">{holidayPackage?.name}</div>
            <div className="text-muted small mb-3">
              Jadwal PH akan menimpa schedule yang sudah ada di tanggal yang dipilih.
            </div>

            {error && <div className="alert alert-danger p-2 small">{error}</div>}

            <label className="form-label">Tanggal PH</label>
            {dates.map((date, index) => (
              <div className="d-flex gap-2 mb-2" key={index}>
                <input
                  className="form-control form-control-sm"
                  type="date"
                  min={holidayPackage?.start_date ? String(holidayPackage.start_date).slice(0, 10) : undefined}
                  max={holidayPackage?.end_date ? String(holidayPackage.end_date).slice(0, 10) : undefined}
                  value={date}
                  required
                  onChange={(e) =>
                    setDates((prev) => prev.map((d, i) => (i === index ? e.target.value : d)))
                  }
                />
                <button
                  type="button"
                  className="btn btn-sm btn-light border"
                  onClick={() => setDates((prev) => prev.filter((_, i) => i !== index))}
                  disabled={dates.length === 1}
                >
                  Hapus
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-sm btn-light border mt-1"
              onClick={() => setDates((prev) => [...prev, ""])}
            >
              + Tambah Tanggal
            </button>
          </div>

          <div className="flex-grow-1 overflow-auto p-3 bg-white mt-2">
            <label className="form-label mb-2">Pilih Staff ({selectedIds.length} dipilih)</label>
            <div className="d-flex gap-2 mb-3">
              <button
                type="button"
                className="btn btn-sm btn-light border"
                onClick={() => setSelectedIds(users.map((u) => u.id))}
              >
                Pilih Semua
              </button>
              <button type="button" className="btn btn-sm btn-light border" onClick={() => setSelectedIds([])}>
                Kosongkan
              </button>
            </div>
            {users.map((user) => (
              <label key={user.id} className="form-check mb-2 user-select-none cursor-pointer">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={selectedIds.includes(user.id)}
                  onChange={() => toggleUser(user.id)}
                />
                <span className="form-check-label d-flex align-items-center gap-2">
                  <span
                    className="avatar avatar-xs rounded bg-primary-lt text-primary fw-bold"
                    style={{ width: "24px", height: "24px" }}
                  >
                    {user.name?.charAt(0)?.toUpperCase()}
                  </span>
                  <span className="text-truncate flex-grow-1">{user.name}</span>
                </span>
              </label>
            ))}
            {users.length === 0 && (
              <div className="text-muted small">Tidak ada staff yang diassign ke package ini.</div>
            )}
          </div>
        </Offcanvas.Body>
        <div className="p-3 border-top bg-white d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-light border" onClick={onClose}>
            Batal
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving || !selectedIds.length || !dates[0]}
          >
            {saving ? "Menyimpan..." : "Terapkan PH"}
          </button>
        </div>
      </form>
    </Offcanvas>
  );
};

const PHHubRecapTab = () => {
  const { users } = useUser();
  const [holidayPackages, setHolidayPackages] = useState([]);
  const [selectedHolidayPackageId, setSelectedHolidayPackageId] = useState("");
  const [holidayAssignments, setHolidayAssignments] = useState([]);
  const [phSchedules, setPhSchedules] = useState([]);
  const [holidayLoading, setHolidayLoading] = useState(false);
  const [holidayError, setHolidayError] = useState("");
  const [showBatchModal, setShowBatchModal] = useState(false);
  
  const [filterDepartmentId, setFilterDepartmentId] = useState("");
  const [filterUserId, setFilterUserId] = useState("");

  const loadHolidayPackages = useCallback(async () => {
    const response = await getRequest("/ahr/holiday-packages?all=1");
    const items = getData(response);
    setHolidayPackages(items);
    setSelectedHolidayPackageId((current) => current || items[0]?.id || "");
  }, []);

  const loadHolidayAssignments = useCallback(async () => {
    setHolidayLoading(true);
    setHolidayError("");

    try {
      const params = new URLSearchParams({ all: "1", include: "user,package" });
      if (selectedHolidayPackageId) params.set("filter[holiday_package_id]", selectedHolidayPackageId);
      if (filterUserId) params.set("filter[user_id]", filterUserId);
      
      const response = await getRequest(`/ahr/holiday-package-assignments?${params.toString()}`);
      setHolidayAssignments(getData(response));

      if (selectedHolidayPackageId) {
        const pkg = holidayPackages.find((p) => String(p.id) === String(selectedHolidayPackageId));
        if (pkg && pkg.start_date && pkg.end_date) {
          const startDate = String(pkg.start_date).slice(0, 10);
          const endDate = String(pkg.end_date).slice(0, 10);
          
          const queryString = buildQueryString({
            all: 1,
            include: "user",
            "filter[date_range]": [startDate, endDate],
          });
          
          const schedResponse = await getRequest(`/ahr/schedules${queryString}`);
          const fetchedSchedules = getData(schedResponse);
          
          setPhSchedules(fetchedSchedules.filter((s) => String(s.holiday_package_id) === String(selectedHolidayPackageId)));
        } else {
          setPhSchedules([]);
        }
      } else {
        setPhSchedules([]);
      }
    } catch (requestError) {
      setHolidayAssignments([]);
      setPhSchedules([]);
      setHolidayError(requestError.message || "Gagal memuat rekap PH dari Holiday Package Assignment.");
    } finally {
      setHolidayLoading(false);
    }
  }, [filterUserId, selectedHolidayPackageId, holidayPackages]);

  useEffect(() => {
    loadHolidayPackages().catch((requestError) => {
      setHolidayPackages([]);
      setHolidayError(requestError.message || "Gagal memuat Holiday Package.");
    });
  }, [loadHolidayPackages]);

  useEffect(() => {
    loadHolidayAssignments();
  }, [loadHolidayAssignments]);

  const filteredUsers = useMemo(() => {
    let result = users.data || [];
    if (filterDepartmentId) result = result.filter((user) => String(user.department?.id) === String(filterDepartmentId));
    if (filterUserId) result = result.filter((user) => String(user.id) === String(filterUserId));
    return result;
  }, [users.data, filterDepartmentId, filterUserId]);

  const groupedUsers = useMemo(() => {
    const groups = {};
    filteredUsers.forEach((user) => {
      const department = user.department?.name || "Unassigned";
      if (!groups[department]) groups[department] = [];
      groups[department].push(user);
    });
    return Object.entries(groups).sort(([first], [second]) => first.localeCompare(second));
  }, [filteredUsers]);

  const publicHolidayGroups = useMemo(() => {
    return groupedUsers.map(([department, employees]) => {
      const rows = employees.map((user) => {
        const userAssignments = holidayAssignments.filter((a) => String(a.user_id) === String(user.id) || String(a.user?.id) === String(user.id));
        const quota = userAssignments.reduce((sum, a) => sum + Number(a.quota ?? a.package?.quota ?? 0), 0);
        
        const usages = phSchedules
          .filter((s) => String(s.user_id) === String(user.id))
          .map((s) => ({
            date: new Date(s.date || s.schedule_date),
            name: holidayPackages.find((p) => String(p.id) === String(s.holiday_package_id))?.name || s.public_holiday_name || "Public Holiday",
          }))
          .sort((a, b) => a.date - b.date);

        const used = userAssignments.reduce((sum, a) => sum + Number(a.used_count ?? 0), 0) || usages.length;
        const remaining = userAssignments.reduce((sum, a) => sum + Number(a.remaining_quota ?? Math.max(Number(a.package?.quota || 0) - a.used_count, 0)), 0) || Math.max(quota - usages.length, 0);

        const packageName = selectedHolidayPackageId 
          ? holidayPackages.find((p) => String(p.id) === String(selectedHolidayPackageId))?.name || "-" 
          : (userAssignments.length > 1 ? "Multiple Packages" : (userAssignments[0]?.package?.name || "-"));

        return {
          user,
          usages,
          quota,
          used,
          remaining,
          packageName,
        };
      });
      return [department, rows];
    });
  }, [groupedUsers, holidayAssignments, phSchedules, holidayPackages, selectedHolidayPackageId]);

  const columnCount = useMemo(() => Math.max(5, ...publicHolidayGroups.flatMap(([, rows]) => rows.map((row) => row.usages.length))), [publicHolidayGroups]);
  const phTotals = useMemo(() => ({
    employees: publicHolidayGroups.reduce((total, [, rows]) => total + rows.filter((r) => r.quota > 0).length, 0),
    usage: publicHolidayGroups.reduce((total, [, rows]) => total + rows.reduce((rowTotal, row) => rowTotal + row.used, 0), 0),
    remaining: publicHolidayGroups.reduce((total, [, rows]) => total + rows.reduce((rowTotal, row) => rowTotal + row.remaining, 0), 0),
  }), [publicHolidayGroups]);

  const selectedPackage = holidayPackages.find(p => String(p.id) === String(selectedHolidayPackageId));
  const assignedUsers = useMemo(() => {
    if (!selectedHolidayPackageId) return [];
    const ids = new Set(holidayAssignments.map(a => a.user?.id || a.user_id).filter(Boolean));
    return publicHolidayGroups.flatMap(([, rows]) => rows.map(r => r.user)).filter(u => ids.has(String(u.id)));
  }, [publicHolidayGroups, holidayAssignments, selectedHolidayPackageId]);

  // Filters Options
  const departmentOptions = useMemo(() => {
    const departments = new Map();
    (users.data || []).forEach((user) => { if (user.department?.id && user.department?.name) departments.set(user.department.id, user.department.name); });
    return [{ value: "", label: "All Departments" }, ...Array.from(departments, ([value, label]) => ({ value, label }))];
  }, [users.data]);

  const userOptions = useMemo(() => {
    return [{ value: "", label: "All Employees" }, ...(users.data || []).filter((user) => !filterDepartmentId || String(user.department?.id) === String(filterDepartmentId)).map((user) => ({ value: user.id, label: user.name }))];
  }, [users.data, filterDepartmentId]);

  return (
    <div className="ph-recap-container">
      <div className="rekap-content-heading px-3 pt-3">
        <div>
          <h3 className="card-title mb-1">Rekap Penggunaan Public Holiday</h3>
          <div className="text-muted small">Quota, pemakaian, dan sisa diambil dari Holiday Package Assignment.</div>
        </div>
        <span className="badge bg-purple-lt text-purple">Kondisional per karyawan</span>
      </div>
      
      {/* Toolbars & Filters */}
      <div className="px-3 pt-3 pb-2 border-bottom bg-light">
        <div className="row g-2 align-items-end">
          <div className="col-md-4">
            <label className="form-label text-muted small fw-bold text-uppercase mb-1">Holiday Package</label>
            <div className="d-flex gap-2">
              <select className="form-select form-select-sm" value={selectedHolidayPackageId} onChange={(event) => setSelectedHolidayPackageId(event.target.value)}>
                <option value="">Semua Package</option>
                {holidayPackages.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
              {selectedHolidayPackageId && (
                <button className="btn btn-sm btn-outline-primary text-nowrap" onClick={() => setShowBatchModal(true)}>
                  Batch PH
                </button>
              )}
            </div>
          </div>
          
          <div className="col-md-3">
            <label className="form-label text-muted small fw-bold text-uppercase mb-1">Departemen</label>
            <Select 
              className="rekap-filter" 
              classNamePrefix="react-select"
              options={departmentOptions} 
              value={departmentOptions.find(opt => String(opt.value) === String(filterDepartmentId)) || departmentOptions[0]} 
              isClearable 
              isSearchable 
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 3000 }) }}
              onChange={(option) => {
                setFilterDepartmentId(option?.value || "");
                setFilterUserId("");
              }} 
            />
          </div>

          <div className="col-md-3">
            <label className="form-label text-muted small fw-bold text-uppercase mb-1">Karyawan</label>
            <Select 
              className="rekap-filter" 
              classNamePrefix="react-select"
              options={userOptions} 
              value={userOptions.find(opt => String(opt.value) === String(filterUserId)) || userOptions[0]} 
              isClearable 
              isSearchable 
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 3000 }) }}
              onChange={(option) => setFilterUserId(option?.value || "")} 
            />
          </div>
        </div>
      </div>
      
      {holidayError && <div className="alert alert-warning m-3">{holidayError}</div>}
      
      <div className="row g-3 px-3 py-3 rekap-stat-row">
        <RecapStat label="Staff Memiliki PH" value={phTotals.employees} icon={<IconUsers size={19} />} tone="purple" />
        <RecapStat label="Total Terpakai" value={phTotals.usage} icon={<IconCalendarEvent size={19} />} tone="primary" />
        <RecapStat label="Sisa Quota" value={phTotals.remaining} icon={<IconInfoCircle size={19} />} tone="success" />
      </div>
      
      <div className="rekap-table-wrap">
        <table className="table table-vcenter card-table rekap-table mb-0">
          <thead>
            <tr>
              <th className="recap-sticky recap-name-col">NAMA</th>
              <th className="recap-sticky recap-department-col">DEPARTEMEN</th>
              <th>PACKAGE</th>
              {Array.from({ length: columnCount }, (_, index) => <th key={index}>{index + 1}</th>)}
              <th>QUOTA</th>
              <th>PAKAI</th>
              <th>SISA</th>
            </tr>
          </thead>
          <tbody>
            {holidayLoading ? (
              <tr>
                <td colSpan={columnCount + 6} className="text-center py-5">
                  <span className="spinner-border spinner-border-sm text-primary me-2" />Memuat rekap PH...
                </td>
              </tr>
            ) : publicHolidayGroups.length > 0 ? publicHolidayGroups.map(([department, rows]) => (
              <React.Fragment key={department}>
                <tr className="recap-group-row">
                  <td className="recap-sticky recap-name-col" colSpan="2">{department}</td>
                  <td colSpan={columnCount + 4} />
                </tr>
                {rows.map(({ user, usages, quota, used, remaining, packageName }) => {
                  return (
                    <tr key={user.id}>
                      <td className="recap-sticky recap-name-col">
                        <div className="d-flex align-items-center">
                          <span className="avatar avatar-xs me-2 rounded bg-primary-lt text-primary fw-bold">{user.name?.charAt(0)?.toUpperCase()}</span>
                          <span className="fw-medium text-wrap">{user.name}</span>
                        </div>
                      </td>
                      <td className="recap-sticky recap-department-col">{user.department?.name || "-"}</td>
                      <td>{packageName}</td>
                      {Array.from({ length: columnCount }, (_, index) => {
                        const usage = usages[index];
                        return <td key={`${user.id}-${index}`}>{usage ? <span className="ph-date" title={usage.name}>{usage.date.toLocaleDateString("id-ID", { day: "2-digit", month: "short" })}</span> : ""}</td>;
                      })}
                      <td className="fw-bold text-blue">{quota}</td>
                      <td className="fw-bold text-primary">{used}</td>
                      <td className="fw-bold text-success">{remaining}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            )) : <EmptyRow colSpan={columnCount + 6} message="Tidak ada assignment public holiday pada package/user yang dipilih." />}
          </tbody>
        </table>
      </div>
      
      {selectedPackage && (
        <BatchUseHolidayModal 
          show={showBatchModal} 
          onClose={() => setShowBatchModal(false)}
          holidayPackage={selectedPackage}
          users={assignedUsers}
          onSuccess={() => {
            setShowBatchModal(false);
            loadHolidayAssignments();
          }}
        />
      )}
    </div>
  );
};

export default PHHubRecapTab;
